import { modelsApi } from '@/api/models.api';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useEffect, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

export interface ProjectUploadingOverlayProps {
  taskId: string | null;
  /** Progress 0–100 when upload queue context drives the task */
  progressFromContext: number;
  /** Open SSE when task id is only known from storage (e.g. refresh) — avoids duplicate stream while context already tracks */
  subscribeToTaskStream: boolean;
  onComplete: () => void;
  onStreamError?: () => void;
}

export const ProjectUploadingOverlay: FC<ProjectUploadingOverlayProps> = ({
  taskId,
  progressFromContext,
  subscribeToTaskStream,
  onComplete,
  onStreamError,
}) => {
  const { t } = useTranslation();
  const [localProgress, setLocalProgress] = useState(0);
  const onCompleteRef = useRef(onComplete);
  const onStreamErrorRef = useRef(onStreamError);
  onCompleteRef.current = onComplete;
  onStreamErrorRef.current = onStreamError;

  useEffect(() => {
    if (!subscribeToTaskStream || !taskId) {
      return;
    }

    const controller = new AbortController();
    void (async () => {
      try {
        await modelsApi.trackProgressSSE(
          taskId,
          (p) => {
            setLocalProgress(p.progress);
          },
          controller.signal
        );
        onCompleteRef.current();
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') {
          return;
        }
        console.error('Project upload overlay SSE error:', e);
        onStreamErrorRef.current?.();
        onCompleteRef.current();
      }
    })();

    return () => controller.abort();
  }, [subscribeToTaskStream, taskId]);

  const progress = subscribeToTaskStream ? localProgress : progressFromContext;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-white/60 px-6 backdrop-blur-sm"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <LoadingSpinner size="large" />
      <p className="max-w-md text-center text-base font-medium text-gray-800">
        {t('projectDetail.processingOverlayTitle')}
      </p>
      <p className="max-w-md text-center text-sm text-gray-600">{t('projectDetail.processingOverlayDescription')}</p>
      <div className="mt-2 w-full max-w-xs">
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${Math.min(100, progress)}%` }}
          />
        </div>
        <p className="mt-1 text-center text-xs text-gray-500">{Math.round(progress)}%</p>
      </div>
    </div>
  );
};
